import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Trophy, Calendar, Table2, TrendingUp, ChevronRight, ChevronLeft,
  Clock, MapPin, Play, CheckCircle, Timer, Download, Camera, X, Image, Facebook, ChevronDown, Radio, Tv
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PushNotificationBanner, { PushNotificationToggle } from "@/components/PushNotificationBanner";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import html2canvas from "html2canvas";
import { getImageUrl } from "@/utils/imageUrl";
import LiveClock from "@/components/common/LiveClock";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  // For date-only strings (YYYY-MM-DD), append T12:00:00 to prevent timezone day shift
  const safeStr = dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr;
  const date = new Date(safeStr);
  return date.toLocaleDateString("es-HN", { day: "numeric", month: "short" });
};

const STATUS_LABELS = {
  scheduled: { label: "Programado", icon: Calendar, color: "bg-blue-100 text-blue-700" },
  live: { label: "En Vivo", icon: Play, color: "bg-red-100 text-red-700 animate-pulse" },
  finished: { label: "Finalizado", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  suspended: { label: "Suspendido", icon: Timer, color: "bg-yellow-100 text-yellow-700" },
  postponed: { label: "Aplazado", icon: Clock, color: "bg-gray-100 text-gray-700" },
};

// Photo Gallery Modal Component for Public View
function MatchPhotoGallery({ match, isOpen, onClose }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  if (!isOpen || !match) return null;
  
  const photos = match.photos || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Fotos del Partido
          </DialogTitle>
        </DialogHeader>
        
        {/* Match Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              {match.home_team?.logo_url && (
                <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
              )}
              <span className="font-medium">{match.home_team?.name}</span>
            </div>
            <span className="text-xl font-bold">
              {match.home_score ?? 0} - {match.away_score ?? 0}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-medium">{match.away_team?.name}</span>
              {match.away_team?.logo_url && (
                <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
              )}
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            {formatDate(match.match_date)} {match.stadium && `• ${match.stadium}`}
          </p>
        </div>
        
        {/* Photos Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div 
                key={index} 
                className="aspect-square cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={getImageUrl(photo.url)}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No hay fotos disponibles</p>
          </div>
        )}
        
        {/* Full Image Preview */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={getImageUrl(selectedPhoto.url)}
              alt="Vista completa"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Tournament Card Component
function TournamentCard({ tournament, compact = false }) {
  return (
    <Link to={`/deportes/torneo/${tournament.slug}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className={`relative overflow-hidden rounded-2xl text-white cursor-pointer ${compact ? 'p-4' : 'p-6'}`}
        style={{ backgroundColor: tournament.color || "#10B981" }}
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          {tournament.logo_url && (
            <img 
              src={getImageUrl(tournament.logo_url)} 
              alt={tournament.name}
              className={`object-contain bg-white/20 rounded-lg p-2 ${compact ? 'w-12 h-12 mb-2' : 'w-16 h-16 mb-3'}`}
            />
          )}
          <h3 className={`font-heading font-bold mb-1 ${compact ? 'text-base' : 'text-lg'}`}>{tournament.name}</h3>
          <p className={`text-white/80 ${compact ? 'text-xs' : 'text-sm'}`}>{tournament.season}</p>
          <div className="flex items-center gap-2 mt-3">
            <Badge className="bg-white/20 text-white border-0 text-xs">
              {tournament.team_ids?.length || 0} equipos
            </Badge>
            <ChevronRight className="h-4 w-4 ml-auto" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

// Featured Tournaments Section with professional slider
function FeaturedTournamentsSection({ tournaments }) {
  const [showAll, setShowAll] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);
  
  // Limit to 10 tournaments max
  const maxTournaments = tournaments.slice(0, 10);
  const visibleCount = 5;
  const hasMore = maxTournaments.length > visibleCount;
  
  // For mobile/tablet: show in scrollable row
  // For desktop: show grid with expand option
  const displayedTournaments = showAll ? maxTournaments : maxTournaments.slice(0, visibleCount);
  
  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 320; // Card width + gap
    const newPosition = direction === 'left' 
      ? Math.max(0, scrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);
    
    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setScrollPosition(newPosition);
  };
  
  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  return (
    <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-brand-text flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Torneos Destacados
            <Badge className="bg-primary/10 text-primary border-0 text-xs ml-2">
              {maxTournaments.length}
            </Badge>
          </h2>
          
          {/* Desktop: Show All Button */}
          {hasMore && (
            <div className="hidden lg:flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                className="text-primary hover:text-primary/80 hover:bg-primary/10"
              >
                {showAll ? 'Ver menos' : `Ver todos (${maxTournaments.length})`}
                <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showAll ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          )}
        </div>
        
        {/* Mobile/Tablet: Horizontal Scroll */}
        <div className="lg:hidden relative">
          {/* Scroll Buttons */}
          {scrollPosition > 0 && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-2 hover:bg-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>
          )}
          {scrollContainerRef.current && scrollPosition < scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth - 10 && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-2 hover:bg-white transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          )}
          
          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {maxTournaments.map((tournament, index) => (
              <div 
                key={tournament.tournament_id} 
                className="flex-shrink-0 w-72 snap-start"
              >
                <TournamentCard tournament={tournament} />
              </div>
            ))}
          </div>
          
          {/* Scroll Indicators */}
          {maxTournaments.length > 2 && (
            <div className="flex justify-center gap-1.5 mt-4">
              {maxTournaments.map((_, index) => (
                <div 
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    Math.floor(scrollPosition / 288) === index 
                      ? 'w-6 bg-primary' 
                      : 'w-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Desktop: Grid with Expand Animation */}
        <div className="hidden lg:block">
          <motion.div 
            layout
            className="grid grid-cols-5 gap-4"
          >
            {displayedTournaments.map((tournament, index) => (
              <motion.div
                key={tournament.tournament_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <TournamentCard tournament={tournament} />
              </motion.div>
            ))}
          </motion.div>
          
          {/* Expand Button at Bottom */}
          {hasMore && !showAll && (
            <div className="flex justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAll(true)}
                className="group border-primary/30 text-primary hover:bg-primary hover:text-white transition-all"
              >
                <span>Ver {maxTournaments.length - visibleCount} torneos más</span>
                <ChevronDown className="h-4 w-4 ml-2 group-hover:translate-y-0.5 transition-transform" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


// Match Card Component with Auto-Clock for Live Matches
function MatchCard({ match, onViewPhotos }) {
  const statusInfo = STATUS_LABELS[match.status] || STATUS_LABELS.scheduled;
  const StatusIcon = statusInfo.icon;
  const hasPhotos = match.photos && match.photos.length > 0;
  const hasFacebookLink = match.facebook_link && match.facebook_link.trim() !== "";
  const isLive = match.status === "live";
  
  // Always use full team names
  const homeTeamName = match.home_team?.name;
  const awayTeamName = match.away_team?.name;
  
  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow ${isLive ? 'ring-2 ring-red-500 ring-opacity-50' : ''}`}>
      {/* Tournament name when live */}
      {isLive && match.tournament?.name && (
        <div className="text-center mb-2">
          <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-full">
            {match.tournament.name}
          </span>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-500">{formatDate(match.match_date)}</span>
        <div className="flex items-center gap-2">
          {hasFacebookLink && (
            <a
              href={match.facebook_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
              title="Ver transmisión en Facebook"
            >
              <Facebook className="h-4 w-4" />
              <span className="hidden sm:inline">En Vivo</span>
            </a>
          )}
          {hasPhotos && (
            <button
              onClick={() => onViewPhotos(match)}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
              title={`Ver ${match.photos.length} foto(s)`}
            >
              <Camera className="h-4 w-4" />
              <span>{match.photos.length}</span>
            </button>
          )}
          <Badge className={`${statusInfo.color} text-xs`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>
      </div>
      
      <div className="flex items-center justify-between gap-4">
        {/* Home Team */}
        <div className="flex-1 text-center">
          {match.home_team?.logo_url && (
            <img 
              src={getImageUrl(match.home_team.logo_url)} 
              alt={match.home_team.name}
              className={`object-contain mx-auto mb-2 ${isLive ? 'w-14 h-14' : 'w-12 h-12'}`}
            />
          )}
          <p className={`font-medium truncate ${isLive ? 'text-base' : 'text-sm'}`}>{homeTeamName}</p>
        </div>
        
        {/* Score */}
        <div className="flex-shrink-0 text-center px-4">
          {match.status === "finished" || match.status === "live" ? (
            <div className="flex items-center gap-2">
              <span className={`font-bold ${isLive ? 'text-3xl text-red-600' : 'text-2xl'}`}>{match.home_score ?? 0}</span>
              <span className="text-gray-400">-</span>
              <span className={`font-bold ${isLive ? 'text-3xl text-red-600' : 'text-2xl'}`}>{match.away_score ?? 0}</span>
            </div>
          ) : (
            <span className="text-lg text-gray-400">{match.match_time || "vs"}</span>
          )}
          {/* Auto-Clock Display for Live Matches */}
          {isLive && <LiveClock match={match} size="sm" />}
        </div>
        
        {/* Away Team */}
        <div className="flex-1 text-center">
          {match.away_team?.logo_url && (
            <img 
              src={getImageUrl(match.away_team.logo_url)} 
              alt={match.away_team.name}
              className={`object-contain mx-auto mb-2 ${isLive ? 'w-14 h-14' : 'w-12 h-12'}`}
            />
          )}
          <p className={`font-medium truncate ${isLive ? 'text-base' : 'text-sm'}`}>{awayTeamName}</p>
        </div>
      </div>
      
      {match.stadium && (
        <div className="flex items-center justify-center gap-1 mt-3 text-xs text-gray-400">
          <MapPin className="h-3 w-3" />
          {match.stadium}
        </div>
      )}
    </div>
  );
}

// Standings Table Component
function StandingsTable({ standings, tournament }) {
  const tableRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Component for last 5 results
  const Last5Results = ({ results }) => {
    if (!results || results.length === 0) return <span className="text-gray-400 text-xs">-</span>;
    
    return (
      <div className="flex items-center gap-1">
        {results.map((result, idx) => (
          <span
            key={idx}
            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              result === 'W' 
                ? 'bg-green-500 text-white' 
                : result === 'D' 
                  ? 'bg-gray-400 text-white' 
                  : 'bg-red-500 text-white'
            }`}
            title={result === 'W' ? 'Ganado' : result === 'D' ? 'Empate' : 'Perdido'}
          >
            {result === 'W' ? '✓' : result === 'D' ? '−' : '✕'}
          </span>
        ))}
      </div>
    );
  };

  const handleExportImage = async () => {
    if (!tableRef.current) return;
    
    setExporting(true);
    try {
      // Create a wrapper with styling for the export
      const wrapper = document.createElement('div');
      wrapper.style.padding = '20px';
      wrapper.style.backgroundColor = '#ffffff';
      wrapper.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      
      // Clone the table
      const tableClone = tableRef.current.cloneNode(true);
      wrapper.appendChild(tableClone);
      
      // Add footer with URL, date and time
      const now = new Date();
      const dateStr = now.toLocaleDateString('es-HN', { 
        day: '2-digit', month: 'long', year: 'numeric' 
      });
      const timeStr = now.toLocaleTimeString('es-HN', { 
        hour: '2-digit', minute: '2-digit', second: '2-digit' 
      });
      
      const footer = document.createElement('div');
      footer.style.marginTop = '16px';
      footer.style.paddingTop = '12px';
      footer.style.borderTop = '1px solid #e5e7eb';
      footer.style.fontSize = '11px';
      footer.style.color = '#6b7280';
      footer.style.textAlign = 'center';
      footer.innerHTML = `
        <div style="font-weight: 600; color: #374151; margin-bottom: 4px;">
          ${tournament?.name || 'Liga Nacional'} - ${tournament?.season || 'Temporada 2026'}
        </div>
        <div>Choluteca.net | ${dateStr} | ${timeStr}</div>
        <div style="margin-top: 4px; font-size: 10px;">
          ${window.location.href}
        </div>
      `;
      wrapper.appendChild(footer);
      
      // Temporarily add to DOM
      document.body.appendChild(wrapper);
      
      // Generate canvas
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      // Remove temporary element
      document.body.removeChild(wrapper);
      
      // Download
      const link = document.createElement('a');
      link.download = `tabla-posiciones-${tournament?.slug || 'liga'}-${now.toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting image:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay datos de tabla disponibles
      </div>
    );
  }
  
  return (
    <div>
      <div ref={tableRef} className="overflow-x-auto bg-white rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-2 font-medium text-gray-600">#</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Equipo</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">PJ</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">G</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">E</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">P</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">GF</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">GC</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">DG</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600 font-bold">PTS</th>
            <th className="text-center py-3 px-2 font-medium text-gray-600">Últ. 5</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((entry, idx) => (
            <tr 
              key={entry.team_id} 
              className={`border-b hover:bg-gray-50 ${idx < 4 ? "bg-green-50/50" : ""}`}
            >
              <td className="py-3 px-2 font-bold text-gray-600">{idx + 1}</td>
              <td className="py-3 px-2">
                <Link 
                  to={`/deportes/equipo/${entry.team?.slug || entry.team_id}`}
                  className="flex items-center gap-2 hover:text-primary"
                >
                  {entry.team?.logo_url && (
                    <img 
                      src={getImageUrl(entry.team.logo_url)} 
                      alt={entry.team.name}
                      className="w-6 h-6 object-contain"
                    />
                  )}
                  <span className="font-medium">{entry.team?.name || "Equipo"}</span>
                </Link>
              </td>
              <td className="py-3 px-2 text-center">{entry.played}</td>
              <td className="py-3 px-2 text-center text-green-600">{entry.won}</td>
              <td className="py-3 px-2 text-center text-yellow-600">{entry.drawn}</td>
              <td className="py-3 px-2 text-center text-red-600">{entry.lost}</td>
              <td className="py-3 px-2 text-center">{entry.goals_for}</td>
              <td className="py-3 px-2 text-center">{entry.goals_against}</td>
              <td className="py-3 px-2 text-center font-medium">
                {entry.goal_difference > 0 ? `+${entry.goal_difference}` : entry.goal_difference}
              </td>
              <td className="py-3 px-2 text-center font-bold text-primary">{entry.points}</td>
              <td className="py-3 px-2">
                <Last5Results results={entry.last_5} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      
      {/* Export Button */}
      <div className="mt-4 flex justify-center">
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleExportImage}
          disabled={exporting}
          className="flex items-center gap-2"
        >
          {exporting ? (
            <>
              <Camera className="h-4 w-4 animate-pulse" />
              Generando imagen...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Descargar como imagen
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default function DeportesPage() {
  const [featuredTournaments, setFeaturedTournaments] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [allTournaments, setAllTournaments] = useState([]); // All tournaments for season filter
  const [selectedTournament, setSelectedTournament] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("primera"); // primera, segunda
  const [selectedSeason, setSelectedSeason] = useState("all"); // Season filter for history
  const [availableSeasons, setAvailableSeasons] = useState([]); // List of unique seasons
  const [liveMatches, setLiveMatches] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [standings, setStandings] = useState(null);
  const [sportsNews, setSportsNews] = useState([]);
  const [todayMatches, setTodayMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoGalleryMatch, setPhotoGalleryMatch] = useState(null);

  useEffect(() => {
    fetchData();
    // Poll for live matches every 30 seconds
    const interval = setInterval(fetchLiveMatches, 30000);
    return () => clearInterval(interval);
  }, []);

  // Re-fetch tournaments when division changes
  useEffect(() => {
    fetchTournamentsByDivision(selectedDivision);
  }, [selectedDivision]);

  useEffect(() => {
    if (selectedTournament) {
      fetchTournamentData(selectedTournament);
    }
  }, [selectedTournament]);

  // Re-fetch recent matches when season filter changes
  useEffect(() => {
    if (selectedSeason !== "all") {
      fetchMatchesBySeason(selectedSeason);
    } else if (selectedTournament) {
      fetchTournamentData(selectedTournament);
    }
  }, [selectedSeason]);

  const fetchLiveMatches = async () => {
    try {
      const res = await axios.get(`${API}/sports/matches/live`);
      setLiveMatches(res.data.items || []);
    } catch (error) {
      console.error("Error fetching live matches:", error);
    }
  };

  const fetchTournamentsByDivision = async (division) => {
    try {
      const res = await axios.get(`${API}/sports/tournaments?status=active&division=${division}`);
      const divTournaments = res.data.items || [];
      setTournaments(divTournaments);
      
      // Auto-select first tournament of this division
      if (divTournaments.length > 0) {
        setSelectedTournament(divTournaments[0].tournament_id);
      } else {
        setSelectedTournament("");
        setStandings(null);
      }
    } catch (error) {
      console.error("Error fetching tournaments by division:", error);
    }
  };

  const fetchMatchesBySeason = async (season) => {
    try {
      // Find tournaments from this season
      const seasonTournaments = allTournaments.filter(t => t.season === season);
      if (seasonTournaments.length === 0) return;
      
      // Fetch matches from all tournaments of this season
      const tournamentIds = seasonTournaments.map(t => t.tournament_id);
      const matchPromises = tournamentIds.map(tid => 
        axios.get(`${API}/sports/matches/recent?tournament_id=${tid}&limit=20`)
      );
      
      const responses = await Promise.all(matchPromises);
      const allMatches = responses.flatMap(r => r.data.items || []);
      
      // Sort by date descending and take first 10
      allMatches.sort((a, b) => {
        const dateA = new Date(a.match_date);
        const dateB = new Date(b.match_date);
        return dateB - dateA;
      });
      
      setRecentMatches(allMatches.slice(0, 10));
    } catch (error) {
      console.error("Error fetching matches by season:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [tournamentsRes, allTournamentsRes, featuredRes, liveRes, newsRes, todayRes] = await Promise.all([
        axios.get(`${API}/sports/tournaments?status=active&division=primera`),
        axios.get(`${API}/sports/tournaments`), // Get all tournaments for seasons
        axios.get(`${API}/sports/tournaments/featured`),
        axios.get(`${API}/sports/matches/live`),
        axios.get(`${API}/sports/news?limit=6`),
        axios.get(`${API}/sports/matches?date_filter=today`),
      ]);
      
      const allTourns = allTournamentsRes.data.items || [];
      setAllTournaments(allTourns);
      
      // Extract unique seasons
      const seasons = [...new Set(allTourns.map(t => t.season).filter(Boolean))];
      seasons.sort((a, b) => b.localeCompare(a)); // Sort descending (newest first)
      setAvailableSeasons(seasons);
      
      setTournaments(tournamentsRes.data.items || []);
      setFeaturedTournaments(featuredRes.data.items || []);
      setLiveMatches(liveRes.data.items || []);
      setSportsNews(newsRes.data.items || []);
      setTodayMatches(todayRes.data.items || []);
      
      // Fetch recent matches from ALL tournaments (both divisions)
      const activeTournaments = allTourns.filter(t => t.status === 'active');
      if (activeTournaments.length > 0) {
        const recentPromises = activeTournaments.map(t => 
          axios.get(`${API}/sports/matches/recent?tournament_id=${t.tournament_id}&limit=10`)
        );
        const recentResponses = await Promise.all(recentPromises);
        const allRecentMatches = recentResponses.flatMap(r => r.data.items || []);
        
        // Sort by date descending
        allRecentMatches.sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
        setRecentMatches(allRecentMatches);
        
        // Fetch upcoming matches from all tournaments
        const upcomingPromises = activeTournaments.map(t => 
          axios.get(`${API}/sports/matches/upcoming?tournament_id=${t.tournament_id}&limit=10`)
        );
        const upcomingResponses = await Promise.all(upcomingPromises);
        const allUpcomingMatches = upcomingResponses.flatMap(r => r.data.items || []);
        
        // Sort by date ascending
        allUpcomingMatches.sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
        setUpcomingMatches(allUpcomingMatches);
      }
      
      // Set default selected tournament (primera división)
      if (tournamentsRes.data.items?.length > 0) {
        setSelectedTournament(tournamentsRes.data.items[0].tournament_id);
      }
    } catch (error) {
      console.error("Error fetching sports data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournamentData = async (tournamentId) => {
    try {
      // Fetch only standings for the selected tournament
      // Recent/upcoming matches are now fetched globally from all tournaments
      const standingsRes = await axios.get(`${API}/sports/standings/${tournamentId}`);
      setStandings(standingsRes.data);
    } catch (error) {
      console.error("Error fetching tournament data:", error);
    }
  };

  return (
    <Layout>
      <PageHero
        section="deportes"
        title="Deportes"
        subtitle="Toda la información del fútbol hondureño: resultados, tablas, noticias y más"
        badge="Liga Nacional"
        badgeIcon={Trophy}
      />

      {/* Live Matches Banner */}
      {liveMatches.length > 0 && (
        <section className="py-4 bg-gradient-to-r from-red-600 to-red-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <span className="flex items-center gap-2 text-white font-bold">
                <Play className="h-5 w-5 animate-pulse" />
                EN VIVO - {liveMatches.length} Partido{liveMatches.length > 1 ? 's' : ''}
              </span>
              <Link to="/deportes/en-vivo">
                <Button size="sm" variant="secondary" className="gap-2 bg-white text-red-600 hover:bg-gray-100">
                  <Tv className="h-4 w-4" />
                  Ver Transmisiones
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveMatches.map((match) => (
                <div key={match.match_id} className="bg-white rounded-xl p-4 shadow-lg">
                  {/* Tournament name */}
                  {match.tournament?.name && (
                    <div className="text-center mb-2">
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                        {match.tournament.name}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-red-100 text-red-700 animate-pulse text-xs">
                      <Play className="h-3 w-3 mr-1" />
                      EN VIVO
                    </Badge>
                    <LiveClock match={match} size="md" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 text-center">
                      {match.home_team?.logo_url && (
                        <img 
                          src={getImageUrl(match.home_team.logo_url)} 
                          alt={match.home_team.name}
                          className="w-12 h-12 object-contain mx-auto mb-1"
                        />
                      )}
                      <p className="font-medium text-sm">{match.home_team?.name}</p>
                    </div>
                    <div className="flex-shrink-0 text-center">
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-red-600">{match.home_score ?? 0}</span>
                        <span className="text-gray-400">-</span>
                        <span className="text-3xl font-bold text-red-600">{match.away_score ?? 0}</span>
                      </div>
                    </div>
                    <div className="flex-1 text-center">
                      {match.away_team?.logo_url && (
                        <img 
                          src={getImageUrl(match.away_team.logo_url)} 
                          alt={match.away_team.name}
                          className="w-12 h-12 object-contain mx-auto mb-1"
                        />
                      )}
                      <p className="font-medium text-sm">{match.away_team?.name}</p>
                    </div>
                  </div>
                  {/* Facebook link if available */}
                  {match.facebook_link && (
                    <div className="mt-3 text-center">
                      <a
                        href={match.facebook_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Facebook className="h-4 w-4" />
                        Ver transmisión
                      </a>
                    </div>
                  )}
                  {/* Link to match detail */}
                  <div className="mt-3 text-center">
                    <Link
                      to={`/deportes/partido/${match.match_id}`}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      Ver detalles y fotos
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Link to Live Transmissions (always visible) */}
      {liveMatches.length === 0 && (
        <section className="py-3 bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <Tv className="h-5 w-5 text-gray-400" />
                <span className="text-sm">¿Hay partidos en vivo? Mira las transmisiones en tiempo real</span>
              </div>
              <Link to="/deportes/en-vivo">
                <Button size="sm" variant="outline" className="gap-2 border-gray-600 text-white hover:bg-gray-700">
                  <Radio className="h-4 w-4" />
                  Ver Transmisiones
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured Tournaments - Professional Slider */}
      {featuredTournaments.length > 0 && (
        <FeaturedTournamentsSection tournaments={featuredTournaments} />
      )}

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Matches & Standings */}
            <div className="lg:col-span-2 space-y-8">
              {/* Tournament Selector & Standings */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-xl font-bold text-brand-text flex items-center gap-2">
                      <Table2 className="h-5 w-5 text-primary" />
                      Tabla de Posiciones
                    </h2>
                  </div>
                  
                  {/* Division and Tournament Selectors */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Division Selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium">División:</span>
                      <div className="flex rounded-lg border border-gray-200 bg-white overflow-hidden" data-testid="division-selector">
                        <button
                          onClick={() => setSelectedDivision("primera")}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            selectedDivision === "primera"
                              ? "bg-primary text-white"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                          data-testid="division-primera"
                        >
                          1ra División
                        </button>
                        <button
                          onClick={() => setSelectedDivision("segunda")}
                          className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                            selectedDivision === "segunda"
                              ? "bg-amber-500 text-white"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                          data-testid="division-segunda"
                        >
                          2da División
                        </button>
                      </div>
                    </div>
                    
                    {/* Tournament Selector */}
                    {tournaments.length > 0 && (
                      <Select value={selectedTournament} onValueChange={setSelectedTournament}>
                        <SelectTrigger className="w-56" data-testid="tournament-selector">
                          <SelectValue placeholder="Seleccionar torneo" />
                        </SelectTrigger>
                        <SelectContent>
                          {tournaments.map((t) => (
                            <SelectItem key={t.tournament_id} value={t.tournament_id}>
                              {t.name} {t.season}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  {/* Division Badge Info */}
                  <div className="flex items-center gap-2">
                    <Badge 
                      className={`text-xs ${
                        selectedDivision === "primera" 
                          ? "bg-primary/10 text-primary border-primary/20" 
                          : "bg-amber-100 text-amber-700 border-amber-200"
                      }`}
                    >
                      {selectedDivision === "primera" ? "Primera División" : "Segunda División"}
                      {tournaments.length > 0 && ` • ${tournaments.length} torneo${tournaments.length > 1 ? 's' : ''}`}
                    </Badge>
                  </div>
                </div>
                
                {standings ? (
                  <StandingsTable standings={standings.standings} tournament={standings.tournament} />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {tournaments.length === 0 
                      ? `No hay torneos activos en la ${selectedDivision === "primera" ? "Primera" : "Segunda"} División`
                      : "Selecciona un torneo para ver la tabla"
                    }
                  </div>
                )}
              </div>

              {/* Recent Results - Organized by Division and Tournament */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <h2 className="font-heading text-xl font-bold text-brand-text flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Resultados Recientes
                  </h2>
                  
                  {/* Season Filter */}
                  {availableSeasons.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Temporada:</span>
                      <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                        <SelectTrigger className="w-40" data-testid="season-filter">
                          <SelectValue placeholder="Todas" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas</SelectItem>
                          {availableSeasons.map((season) => (
                            <SelectItem key={season} value={season}>
                              {season}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {selectedSeason !== "all" && (
                  <div className="mb-4">
                    <Badge className="bg-blue-100 text-blue-700 border-0">
                      Mostrando partidos de la temporada {selectedSeason}
                    </Badge>
                  </div>
                )}
                
                {/* Results grouped by Division */}
                {recentMatches.length > 0 ? (
                  <div className="space-y-6">
                    {/* Group matches by tournament and then display by division */}
                    {(() => {
                      // Group matches by tournament_id
                      const matchesByTournament = recentMatches.reduce((acc, match) => {
                        const tid = match.tournament_id;
                        if (!acc[tid]) acc[tid] = [];
                        acc[tid].push(match);
                        return acc;
                      }, {});
                      
                      // Get tournament info and separate by division
                      const primeraMatches = [];
                      const segundaMatches = [];
                      
                      Object.entries(matchesByTournament).forEach(([tid, matches]) => {
                        const tournament = allTournaments.find(t => t.tournament_id === tid);
                        const division = tournament?.division || 'primera';
                        const tournamentData = {
                          tournament,
                          matches,
                          name: tournament?.name || 'Torneo',
                          season: tournament?.season || ''
                        };
                        
                        if (division === 'segunda') {
                          segundaMatches.push(tournamentData);
                        } else {
                          primeraMatches.push(tournamentData);
                        }
                      });
                      
                      return (
                        <>
                          {/* Primera División Results */}
                          {primeraMatches.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-primary/20">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Primera División
                                </Badge>
                              </div>
                              {primeraMatches.map(({ tournament, matches, name, season }) => (
                                <div key={tournament?.tournament_id || name} className="bg-white rounded-xl border overflow-hidden">
                                  <div 
                                    className="px-4 py-2 border-b flex items-center gap-2 text-white text-sm font-medium"
                                    style={{ backgroundColor: tournament?.color || '#10B981' }}
                                  >
                                    {tournament?.logo_url && (
                                      <img src={getImageUrl(tournament.logo_url)} alt="" className="w-5 h-5 object-contain" />
                                    )}
                                    <span>{name}</span>
                                    <span className="opacity-75">• {season}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                                    {matches.slice(0, 6).map((match) => (
                                      <MatchCard key={match.match_id} match={match} onViewPhotos={setPhotoGalleryMatch} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Segunda División Results */}
                          {segundaMatches.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Segunda División
                                </Badge>
                              </div>
                              {segundaMatches.map(({ tournament, matches, name, season }) => (
                                <div key={tournament?.tournament_id || name} className="bg-white rounded-xl border overflow-hidden">
                                  <div 
                                    className="px-4 py-2 border-b flex items-center gap-2 text-white text-sm font-medium"
                                    style={{ backgroundColor: tournament?.color || '#F59E0B' }}
                                  >
                                    {tournament?.logo_url && (
                                      <img src={getImageUrl(tournament.logo_url)} alt="" className="w-5 h-5 object-contain" />
                                    )}
                                    <span>{name}</span>
                                    <span className="opacity-75">• {season}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                                    {matches.slice(0, 6).map((match) => (
                                      <MatchCard key={match.match_id} match={match} onViewPhotos={setPhotoGalleryMatch} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                    No hay resultados recientes{selectedSeason !== "all" ? ` para la temporada ${selectedSeason}` : ""}
                  </div>
                )}
              </div>

              {/* Upcoming Matches - Organized by Division and Tournament */}
              <div>
                <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  Próximos Partidos
                </h2>
                {upcomingMatches.length > 0 ? (
                  <div className="space-y-6">
                    {(() => {
                      // Group matches by tournament_id
                      const matchesByTournament = upcomingMatches.reduce((acc, match) => {
                        const tid = match.tournament_id;
                        if (!acc[tid]) acc[tid] = [];
                        acc[tid].push(match);
                        return acc;
                      }, {});
                      
                      // Get tournament info and separate by division
                      const primeraMatches = [];
                      const segundaMatches = [];
                      
                      Object.entries(matchesByTournament).forEach(([tid, matches]) => {
                        const tournament = allTournaments.find(t => t.tournament_id === tid);
                        const division = tournament?.division || 'primera';
                        const tournamentData = {
                          tournament,
                          matches,
                          name: tournament?.name || 'Torneo',
                          season: tournament?.season || ''
                        };
                        
                        if (division === 'segunda') {
                          segundaMatches.push(tournamentData);
                        } else {
                          primeraMatches.push(tournamentData);
                        }
                      });
                      
                      return (
                        <>
                          {/* Primera División Upcoming */}
                          {primeraMatches.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-primary/20">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Primera División
                                </Badge>
                              </div>
                              {primeraMatches.map(({ tournament, matches, name, season }) => (
                                <div key={tournament?.tournament_id || name} className="bg-white rounded-xl border overflow-hidden">
                                  <div 
                                    className="px-4 py-2 border-b flex items-center gap-2 text-white text-sm font-medium"
                                    style={{ backgroundColor: tournament?.color || '#10B981' }}
                                  >
                                    {tournament?.logo_url && (
                                      <img src={getImageUrl(tournament.logo_url)} alt="" className="w-5 h-5 object-contain" />
                                    )}
                                    <span>{name}</span>
                                    <span className="opacity-75">• {season}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                                    {matches.slice(0, 6).map((match) => (
                                      <MatchCard key={match.match_id} match={match} onViewPhotos={setPhotoGalleryMatch} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Segunda División Upcoming */}
                          {segundaMatches.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Segunda División
                                </Badge>
                              </div>
                              {segundaMatches.map(({ tournament, matches, name, season }) => (
                                <div key={tournament?.tournament_id || name} className="bg-white rounded-xl border overflow-hidden">
                                  <div 
                                    className="px-4 py-2 border-b flex items-center gap-2 text-white text-sm font-medium"
                                    style={{ backgroundColor: tournament?.color || '#F59E0B' }}
                                  >
                                    {tournament?.logo_url && (
                                      <img src={getImageUrl(tournament.logo_url)} alt="" className="w-5 h-5 object-contain" />
                                    )}
                                    <span>{name}</span>
                                    <span className="opacity-75">• {season}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                                    {matches.slice(0, 6).map((match) => (
                                      <MatchCard key={match.match_id} match={match} onViewPhotos={setPhotoGalleryMatch} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                    No hay partidos programados
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Today's Matches & News */}
            <div>
              {/* Today's Matches */}
              {todayMatches.length > 0 && (
                <div className="mb-8" data-testid="today-matches">
                  <h2 className="font-heading text-xl font-bold text-brand-text mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-red-500" />
                    Partidos de Hoy
                  </h2>
                  <div className="space-y-3">
                    {todayMatches.map((match) => {
                      const isLive = match.status === "live";
                      const isFinished = match.status === "finished";
                      const isScheduled = match.status === "scheduled";
                      return (
                        <Link
                          key={match.match_id}
                          to={`/deportes/partido/${match.match_id}`}
                          className={`block bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${isLive ? "ring-2 ring-red-400 ring-opacity-60" : ""}`}
                          data-testid={`today-match-${match.match_id}`}
                        >
                          {/* Status badge */}
                          <div className="flex items-center justify-between mb-3">
                            {match.tournament?.name && (
                              <span className="text-[11px] text-gray-400 truncate">{match.tournament.name}</span>
                            )}
                            <Badge className={`text-[10px] ${isLive ? "bg-red-100 text-red-700 animate-pulse" : isFinished ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                              {isLive ? (
                                <><Play className="h-2.5 w-2.5 mr-1" />En Vivo</>
                              ) : isFinished ? (
                                <><CheckCircle className="h-2.5 w-2.5 mr-1" />Finalizado</>
                              ) : (
                                <><Clock className="h-2.5 w-2.5 mr-1" />{match.match_time || "Por definir"}</>
                              )}
                            </Badge>
                          </div>
                          
                          {/* Live Clock for today's matches */}
                          {isLive && (
                            <div className="mb-3">
                              <LiveClock match={match} size="sm" />
                            </div>
                          )}

                          {/* Teams */}
                          <div className="space-y-2.5">
                            {/* Home team */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                {match.home_team?.logo_url ? (
                                  <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-7 h-7 object-contain flex-shrink-0" />
                                ) : (
                                  <div className="w-7 h-7 bg-gray-100 rounded-full flex-shrink-0" />
                                )}
                                <span className={`text-sm truncate ${isLive ? "font-bold" : "font-medium"} text-gray-900`}>
                                  {match.home_team?.name || "Local"}
                                </span>
                              </div>
                              {(isFinished || isLive) && (
                                <span className={`text-lg font-bold tabular-nums ml-2 ${isLive ? "text-red-600" : "text-gray-900"}`}>
                                  {match.home_score ?? 0}
                                </span>
                              )}
                            </div>

                            {/* Away team */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                {match.away_team?.logo_url ? (
                                  <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-7 h-7 object-contain flex-shrink-0" />
                                ) : (
                                  <div className="w-7 h-7 bg-gray-100 rounded-full flex-shrink-0" />
                                )}
                                <span className={`text-sm truncate ${isLive ? "font-bold" : "font-medium"} text-gray-900`}>
                                  {match.away_team?.name || "Visitante"}
                                </span>
                              </div>
                              {(isFinished || isLive) && (
                                <span className={`text-lg font-bold tabular-nums ml-2 ${isLive ? "text-red-600" : "text-gray-900"}`}>
                                  {match.away_score ?? 0}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Stadium */}
                          {match.stadium && (
                            <div className="flex items-center gap-1 mt-2.5 text-[11px] text-gray-400">
                              <MapPin className="h-3 w-3" />
                              {match.stadium}
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* News */}
              <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Noticias Deportivas
              </h2>
              {sportsNews.length > 0 ? (
                <div className="space-y-4">
                  {sportsNews.map((post) => (
                    <Link 
                      key={post.post_id} 
                      to={`/noticias/${post.slug}`}
                      className="flex gap-3 bg-white rounded-xl border p-3 hover:shadow-md transition-shadow"
                    >
                      {/* Thumbnail */}
                      <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                        {post.featured_image_url ? (
                          <img 
                            src={getImageUrl(post.featured_image_url)} 
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Trophy className="h-6 w-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-brand-text line-clamp-2 text-sm mb-1">
                          {post.title}
                        </h3>
                        <p className="text-xs text-gray-500 line-clamp-2 mb-1">
                          {post.excerpt}
                        </p>
                        <span className="text-xs text-gray-400">
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                  No hay noticias deportivas
                </div>
              )}
              
              {/* All Teams Link */}
              <div className="mt-8 bg-gray-50 rounded-xl p-6">
                <h3 className="font-heading font-bold text-brand-text mb-4">Equipos</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Explora todos los equipos de la Liga Nacional
                </p>
                <Link to="/deportes/equipos">
                  <Button variant="outline" className="w-full">
                    Ver todos los equipos
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Photo Gallery Modal */}
      <MatchPhotoGallery 
        match={photoGalleryMatch} 
        isOpen={!!photoGalleryMatch} 
        onClose={() => setPhotoGalleryMatch(null)} 
      />
      
      {/* Push Notification Banner */}
      <PushNotificationBanner />

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
